// TaskContext.js - 백그라운드 작업 관리 Context
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const TaskContext = createContext();

export const useTasks = () => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error('useTasks must be used within a TaskProvider');
  }
  return context;
};

export const TaskProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const [runningTasks, setRunningTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const [acknowledgedTasks, setAcknowledgedTasks] = useState(new Set()); // 확인한 작업 ID
  const [taskApiOffline, setTaskApiOffline] = useState(false);
  const hasWarnedOfflineRef = React.useRef(false);

  // 상대 경로 사용
  const TASK_API_BASE = process.env.REACT_APP_TASK_API_URL || '/api/v3';

  const handleTaskApiError = useCallback(
    (error, context = '') => {
      if (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
        if (!hasWarnedOfflineRef.current) {
          console.warn(`Task API (${TASK_API_BASE})에 연결할 수 없습니다. ${context}`.trim());
          hasWarnedOfflineRef.current = true;
        }
        setIsPolling(false);
        setRunningTasks([]);
        setTaskApiOffline(true);
      } else if (error?.response?.status === 404) {
        console.warn(`요청한 작업 엔드포인트를 찾을 수 없습니다. ${context}`.trim());
      } else if (context) {
        console.error(context, error);
      } else {
        console.error(error);
      }
    },
    [TASK_API_BASE]
  );

  // 작업 목록 조회
  const fetchTasks = useCallback(async (includeCompleted = true) => {
    if (taskApiOffline) {
      return;
    }
    try {
      const response = await axios.get(`${TASK_API_BASE}/tasks`, {
        params: {
          include_completed: includeCompleted,
          limit: 50
        }
      });
      
      // 응답 데이터가 배열인지 확인 (HTML이 반환된 경우 대비)
      let allTasks = [];
      if (Array.isArray(response.data)) {
        allTasks = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // 객체인 경우 배열로 변환 시도
        console.warn('⚠️ Tasks API 응답이 배열이 아닙니다:', typeof response.data);
        allTasks = [];
      } else {
        console.error('❌ Tasks API 응답 형식 오류:', typeof response.data);
        allTasks = [];
      }
      
      if (taskApiOffline) {
        setTaskApiOffline(false);
        hasWarnedOfflineRef.current = false;
      }
      setTasks(allTasks);
      // 완료된 작업만 필터링
      const completed = allTasks.filter(t => t.status === 'completed');
      setCompletedTasks(completed);
    } catch (error) {
      handleTaskApiError(error, 'Failed to fetch tasks:');
    }
  }, [TASK_API_BASE, handleTaskApiError, taskApiOffline]);

  // 실행 중인 작업만 조회
  const fetchRunningTasks = useCallback(async () => {
    if (taskApiOffline) {
      return;
    }
    try {
      const response = await axios.get(`${TASK_API_BASE}/tasks/running`);
      setRunningTasks(response.data || []);
      if (taskApiOffline) {
        setTaskApiOffline(false);
        hasWarnedOfflineRef.current = false;
      }
    } catch (error) {
      handleTaskApiError(error, 'Failed to fetch running tasks:');
    }
  }, [TASK_API_BASE, handleTaskApiError, taskApiOffline]);

  // 특정 작업 조회
  const getTask = useCallback(async (taskId) => {
    if (taskApiOffline) {
      return null;
    }
    try {
      const response = await axios.get(`${TASK_API_BASE}/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      handleTaskApiError(error, 'Failed to get task:');
      return null;
    }
  }, [TASK_API_BASE, handleTaskApiError, taskApiOffline]);

  // 작업 취소
  const cancelTask = useCallback(async (taskId) => {
    if (taskApiOffline) {
      return false;
    }
    try {
      await axios.post(`${TASK_API_BASE}/tasks/${taskId}/cancel`);
      await fetchTasks();
      await fetchRunningTasks();
      return true;
    } catch (error) {
      handleTaskApiError(error, 'Failed to cancel task:');
      return false;
    }
  }, [TASK_API_BASE, fetchTasks, fetchRunningTasks, handleTaskApiError, taskApiOffline]);

  // 작업 삭제
  const deleteTask = useCallback(async (taskId) => {
    if (taskApiOffline) {
      return false;
    }
    try {
      await axios.delete(`${TASK_API_BASE}/tasks/${taskId}`);
      await fetchTasks();
      await fetchRunningTasks();
      return true;
    } catch (error) {
      handleTaskApiError(error, 'Failed to delete task:');
      return false;
    }
  }, [TASK_API_BASE, fetchTasks, fetchRunningTasks, handleTaskApiError, taskApiOffline]);

  // 완료된 작업 일괄 정리
  const clearCompletedTasks = useCallback(async () => {
    if (taskApiOffline) {
      return false;
    }
    try {
      const response = await axios.post(`${TASK_API_BASE}/tasks/clear-completed`);
      console.log('✅ 완료된 작업 정리:', response.data);
      await fetchTasks();
      return true;
    } catch (error) {
      handleTaskApiError(error, '❌ 완료된 작업 정리 실패:');
      return false;
    }
  }, [TASK_API_BASE, fetchTasks, handleTaskApiError, taskApiOffline]);

  // 백그라운드 작업 시작 (파일 리뷰)
  const startFileReview = useCallback(async (file) => {
    if (taskApiOffline) {
      return null;
    }
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${TASK_API_BASE}/files/review-async`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setIsPolling(true);
        await fetchTasks();
        await fetchRunningTasks();
        setTimeout(async () => {
          await fetchRunningTasks();
          await fetchTasks();
        }, 300);
        return response.data.task_id;
      }
      return null;
    } catch (error) {
      handleTaskApiError(error, 'Failed to start file review:');
      return null;
    }
  }, [TASK_API_BASE, fetchTasks, fetchRunningTasks, handleTaskApiError, taskApiOffline]);

  const startFileMerge = useCallback(async (tableNames, outputTable, mergeType) => {
    if (taskApiOffline) {
      return null;
    }
    try {
      const response = await axios.post(`${TASK_API_BASE}/files/merge-async`, {
        table_names: tableNames,
        output_table: outputTable,
        merge_type: mergeType
      });

      if (response.data.success) {
        await fetchTasks();
        await fetchRunningTasks();
        return response.data.task_id;
      }
      return null;
    } catch (error) {
      handleTaskApiError(error, 'Failed to start file merge:');
      return null;
    }
  }, [TASK_API_BASE, fetchTasks, fetchRunningTasks, handleTaskApiError, taskApiOffline]);

  const startDataCleaning = useCallback(async (tableName, columnName, pattern, replacement, outputColumn = null) => {
    if (taskApiOffline) {
      return null;
    }
    try {
      const response = await axios.post(`${TASK_API_BASE}/clean/regex-async`, {
        table_name: tableName,
        column_name: columnName,
        pattern: pattern,
        replacement: replacement,
        output_column: outputColumn
      });

      if (response.data.success) {
        await fetchTasks();
        await fetchRunningTasks();
        return response.data.task_id;
      }
      return null;
    } catch (error) {
      handleTaskApiError(error, 'Failed to start data cleaning:');
      return null;
    }
  }, [TASK_API_BASE, fetchTasks, fetchRunningTasks, handleTaskApiError, taskApiOffline]);

  // 폴링 시작/중지
  const startPolling = useCallback(() => {
    setIsPolling(true);
  }, []);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
  }, []);

  // 샘플 작업 추가 (데모용)
  const addSampleTask = useCallback((status = 'completed', progress = 100, category = null) => {
    // 카테고리가 지정되지 않으면 랜덤하게 할당
    const categories = ['files', 'file-review'];
    const taskCategory = category || categories[Math.floor(Math.random() * categories.length)];
    
    const sampleTask = {
      id: `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `샘플 작업 ${tasks.length + 1} (${taskCategory === 'files' ? '데이터 병합' : '파일 리뷰'})`,
      status: status,
      progress: progress,
      timestamp: new Date().toISOString(),
      category: taskCategory, // 작업 카테고리 추가
      // 완료된 작업에는 샘플 파일 다운로드 URL 추가
      fileUrl: status === 'completed' ? `/api/v3/files/download/sample_${Date.now()}.xlsx` : null,
      fileName: status === 'completed' ? `작업결과_${tasks.length + 1}.xlsx` : null
    };
    setTasks(prev => [...prev, sampleTask]);
    if (status === 'running') {
      setRunningTasks(prev => [...prev, sampleTask]);
    } else if (status === 'completed') {
      setCompletedTasks(prev => [...prev, sampleTask]);
    }
  }, [tasks.length]);

  // 특정 작업 확인 처리
  const acknowledgeTask = useCallback((taskId) => {
    setAcknowledgedTasks(prev => new Set([...prev, taskId]));
  }, []);

  // 모든 완료된 작업 확인 처리
  const acknowledgeAllCompleted = useCallback(() => {
    const completedIds = tasks
      .filter(t => t.status === 'completed')
      .map(t => t.id);
    setAcknowledgedTasks(prev => new Set([...prev, ...completedIds]));
  }, [tasks]);

  // 메뉴별 미확인 작업 개수 계산
  const getUnacknowledgedCount = useCallback((menuId) => {
    // tasks가 배열이 아닌 경우 빈 배열로 처리
    if (!Array.isArray(tasks)) {
      return 0;
    }
    
    // type과 menuId 매핑
    const typeMapping = {
      'files': ['file_merge', 'file_join'],
      'file-review': ['file_review'],
      'clean': ['data_cleaning']
    };
    
    const typesForMenu = typeMapping[menuId] || [];
    
    return tasks.filter(t => 
      t.status === 'completed' && 
      !acknowledgedTasks.has(t.id) &&
      (menuId ? typesForMenu.includes(t.type) : true)
    ).length;
  }, [tasks, acknowledgedTasks]);

  // 전체 미확인 완료 작업 개수 (useMemo로 자동 재계산)
  const unacknowledgedCompletedCount = useMemo(() => {
    // tasks가 배열이 아닌 경우 0 반환
    if (!Array.isArray(tasks)) {
      return 0;
    }
    return tasks.filter(t => 
      t.status === 'completed' && 
      !acknowledgedTasks.has(t.id)
    ).length;
  }, [tasks, acknowledgedTasks]);

  // 폴링: 실행 중인 작업이 있으면 주기적으로 업데이트
  useEffect(() => {
    let intervalId;

    if (!taskApiOffline && (isPolling || runningTasks.length > 0)) {
      // 초기 로드
      fetchRunningTasks();

      // 폴링 간격을 30초로 증가 (불필요한 로그 방지)
      // 실행 중인 작업이 있을 때만 주기적으로 업데이트
      intervalId = setInterval(() => {
        fetchRunningTasks();
        fetchTasks(true); // 완료된 작업도 포함하여 업데이트
      }, 30000); // 30초마다 업데이트
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPolling, runningTasks.length, fetchRunningTasks, fetchTasks, taskApiOffline]); // runningTasks 길이 포함

  // 초기 로드
  useEffect(() => {
    if (!taskApiOffline) {
      fetchTasks();
      fetchRunningTasks();
    }
  }, [fetchTasks, fetchRunningTasks, taskApiOffline]);

  const value = {
    tasks,
    runningTasks,
    completedTasks,
    fetchTasks,
    fetchRunningTasks,
    getTask,
    cancelTask,
    deleteTask,
    clearCompletedTasks,
    startFileReview,
    startFileMerge,
    startDataCleaning,
    startPolling,
    stopPolling,
    isPolling,
    addSampleTask,
    acknowledgeTask,
    acknowledgeAllCompleted,
    getUnacknowledgedCount,
    unacknowledgedCompletedCount,
    taskApiOffline
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

